import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH, acceptInvitationSchema, messages } from '@nesc/shared';
import { api, errorMessage } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { AuthShell } from './auth-shell';

/** S02 招待受諾・パスワード設定 */
export const InviteAcceptPage = () => {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setErrors({});
    setFormError(null);

    const parsed = acceptInvitationSchema.safeParse({ token, name, password, passwordConfirm });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        fieldErrors[issue.path.join('.')] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setIsPending(true);
    try {
      await api.post('/auth/invitations/accept', parsed.data);
      navigate('/login', { replace: true });
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setIsPending(false);
    }
  };

  return (
    <AuthShell title={messages.auth.inviteTitle} description="お名前とパスワードを設定してください">
      <form onSubmit={submit} className="space-y-4" noValidate>
        {formError && (
          <p role="alert" className="rounded-control border-2 border-foreground bg-muted p-3 text-body font-semibold">
            {formError}
          </p>
        )}

        <Field label={messages.auth.displayName} required error={errors.name}>
          {(props) => (
            <Input {...props} autoComplete="name" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
          )}
        </Field>

        <Field label={messages.auth.password} required error={errors.password} hint={messages.auth.passwordRule}>
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>

        <Field label={messages.auth.passwordConfirm} required error={errors.passwordConfirm}>
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
            />
          )}
        </Field>

        <Button type="submit" size="lg" className="w-full" disabled={isPending}>
          {isPending ? messages.common.loading : messages.auth.inviteSubmit}
        </Button>
      </form>
    </AuthShell>
  );
};
